import { useCallback, useEffect, useRef } from 'react'

interface TimerProps {
  duration: number
  onTimerExpired: () => void
}

const Timer = ({ onTimerExpired, duration }: TimerProps): null => {
  const timer = useRef<NodeJS.Timeout | null>(null)

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current)
  }

  const startTimer = useCallback(
    (duration: number) => {
      clearTimer()
      timer.current = setTimeout(onTimerExpired, duration * 1000)
    },
    [onTimerExpired]
  )

  useEffect(() => {
    // Defer timer start until after initial page load
    const deferTimer = () => {
      if (document.readyState === 'complete') {
        startTimer(duration)
      } else {
        window.addEventListener('load', () => startTimer(duration))
      }
    }

    // Use requestIdleCallback if available, otherwise use setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(deferTimer)
    } else {
      setTimeout(deferTimer, 0)
    }

    return () => {
      clearTimer()
      window.removeEventListener('load', () => startTimer(duration))
    }
  }, [duration, startTimer])

  useEffect(() => {
    startTimer(duration)
  }, [duration, startTimer])

  return null
}

export default Timer
